// src/components/Authentication/Login.js

import React, { useState, useEffect, useContext } from 'react';
import { auth } from '../../firebase';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import CustomModal from '../Common/CustomModal';
import './Login.css';
import { useTranslation } from 'react-i18next';
import Spinner from '../Common/Spinner';
import CatholicoreLogo from '../../assets/Catholicore_inline_transparent.png';

// Import contexts
import { DataContext } from '../../DataContext';
import { useColorSettings } from '../Common/ColorSettingsContext';
import { useLanguageSettings } from '../Common/LanguageSettingsContext';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [user, loadingAuth, errorAuth] = useAuthState(auth);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  // Access appSettings from DataContext
  const {appSettingsLoading } = useContext(DataContext);

  // Access color settings
  const { appSettings, loading: colorLoading } = useColorSettings();

  // Access language settings
  const { loading: languageLoading } = useLanguageSettings();

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setShowModal(false);

    try {
      await auth.signInWithEmailAndPassword(email, password);
      // Navigation is handled by useAuthState
    } catch (error) {
      console.error('Authentication Error:', error);
      setMessage(t('login.authenticationError'));
      setShowModal(true);
    }
  };

  // Combined loading state
  const isLoading = loadingAuth || appSettingsLoading || colorLoading || languageLoading;

  // Handle loading state
  if (isLoading) {
    return (
      <div className="login-background">
        <div className="login-container">
          <div className="login-logo">
            <img
              src={
                appSettings?.logoUrl ||
                CatholicoreLogo
              }
              height="150px"
              alt={t('login.logoAlt')}
            />
          </div>
          <h2>{t('login.title')}</h2>
          <Spinner />
        </div>
      </div>
    );
  }

  // Handle authentication error
  if (errorAuth) {
    return (
      <div className="login-background">
        <div className="login-container">
          <div className="login-logo">
            <img
              src={
                appSettings?.logoUrl ||
                CatholicoreLogo
              }
              height="150px"
              alt={t('login.logoAlt')}
            />
          </div>
          <h2>{t('login.title')}</h2>
          <p>{t('login.authError', { error: errorAuth.message })}</p>
        </div>
      </div>
    );
  }

  // Redirect if user is authenticated
  if (user) {
    return <Navigate to="/main" replace />;
  }

  // Render the login form
  return (
    <div className="login-background">
      <div className="login-container">
        <div className="login-logo">
          <img
            src={
              appSettings?.logoUrl ||
              CatholicoreLogo
            }
            height="150px"
            alt={t('login.logoAlt')}
          />
        </div>
        <h2>{t('login.title')}</h2>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-input-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              aria-label={t('login.emailLabel')}
            />
          </div>
          <div className="login-input-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              required
              minLength={6}
              aria-label={t('login.passwordLabel')}
            />
          </div>
          <button type="submit" className="login-submit-button">
            {t('login.submitButton')}
          </button>
        </form>
        <br />
        <h6 onClick={() => navigate('/password-recovery')} className="login-forgot-password">
          {t('login.forgotPassword')}
        </h6>
      </div>
      <CustomModal
        show={showModal}
        onClose={handleCloseModal}
        title={t('login.modalTitle')}
      >
        <p className='login-message'>{message}</p>
      </CustomModal>
    </div>
  );
};

export default Login;
